import { DatePicker } from '@/components/ui/date-picker';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { formatCurrency, formatMonth, getStatusLabel, getPaymentTypeLabel } from '../lib/utils';
import axios from 'axios';
import { Link } from 'react-router-dom';
import RoomGallery from '../components/RoomGallery';
import { 
  DoorOpen, 
  Users, 
  CreditCard, 
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Home,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  PiggyBank,
  BarChart3,
  Zap
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, value, label, color = 'primary', loading }) => (
  <Card className="stat-card card-hover p-4" data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl bg-${color}/10 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 text-${color}`} />
        </div>
      </div>
      {loading ? (
        <>
          <Skeleton className="h-8 w-24 mb-1" />
          <Skeleton className="h-4 w-20" />
        </>
      ) : (
        <>
          <p className="text-2xl font-bold tracking-tight">{value}</p>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{label}</p>
        </>
      )}
    </div>
  </Card>
);

const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const OwnerDashboard = () => {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodFilter, setPeriodFilter] = useState('all');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  // Calculate date range based on filter
  const getDateRange = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    
    switch (periodFilter) {
      case 'all':
        return {
          start: null,
          end: null,
          label: 'Semua Periode'
        };
      case 'this_month':
        return {
          start: new Date(year, month, 1),
          end: new Date(year, month + 1, 0),
          label: 'Bulan Ini'
        };
      case 'last_month':
        return {
          start: new Date(year, month - 1, 1),
          end: new Date(year, month, 0),
          label: 'Bulan Kemarin'
        };
      case 'last_3_months':
        return {
          start: new Date(year, month - 2, 1),
          end: new Date(year, month + 1, 0),
          label: '3 Bulan Terakhir'
        };
      case 'last_6_months':
        return {
          start: new Date(year, month - 5, 1),
          end: new Date(year, month + 1, 0),
          label: '6 Bulan Terakhir'
        };
      case 'custom':
        return {
          start: customRange.start ? new Date(customRange.start) : new Date(year, month, 1),
          end: customRange.end ? new Date(customRange.end) : new Date(year, month + 1, 0),
          label: 'Custom'
        };
      default:
        return {
          start: new Date(year, month, 1),
          end: new Date(year, month + 1, 0),
          label: 'Bulan Ini'
        };
    }
  };

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const range = getDateRange();
      
      let dashboardUrl = `${API_URL}/api/reports/dashboard`;
      let analyticsUrl = `${API_URL}/api/reports/analytics`;
      
      if (range.start && range.end) {
        const startDate = range.start.toISOString().split('T')[0];
        const endDate = range.end.toISOString().split('T')[0];
        dashboardUrl += `?start_date=${startDate}&end_date=${endDate}`;
        analyticsUrl += `?start_date=${startDate}&end_date=${endDate}`;
      }
      
      const [dashboardRes, analyticsRes] = await Promise.all([
        axios.get(dashboardUrl),
        axios.get(analyticsUrl)
      ]);
      setData(dashboardRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodFilter, customRange]);

  const periodOptions = [
    { value: 'all', label: 'Semua Periode' },
    { value: 'this_month', label: 'Bulan Ini' },
    { value: 'last_month', label: 'Bulan Kemarin' },
    { value: 'last_3_months', label: '3 Bulan Terakhir' },
    { value: 'last_6_months', label: '6 Bulan Terakhir' },
    { value: 'custom', label: 'Custom' },
  ];

  const chartData = data?.monthly_income?.map(item => ({
    month: formatMonth(item.month).split(' ')[0],
    Pendapatan: item.amount
  })) || [];

  const incomeTrendData = analytics?.income_trend?.map(item => ({
    month: item.month_name,
    income: item.income
  })) || [];

  const handlePeriodChange = (value) => {
    setPeriodFilter(value);
    if (value === 'custom') {
      setShowCustomPicker(false);
    } else {
      setShowCustomPicker(false);
    }
  };

  const handleCustomRangeApply = () => {
    if (customRange.start && customRange.end) {
      setShowCustomPicker(false);
      // Trigger refetch
      fetchDashboard();
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid={isAdmin() ? 'admin-dashboard' : 'owner-dashboard'}>
      {/* Header with Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{isAdmin() ? 'Dashboard Admin' : 'Dashboard'}</h1>
          <p className="text-muted-foreground text-sm mt-1">Selamat datang di Kos Cemara</p>
        </div>
        
        {/* Period Filter */}
        <div className="flex items-center gap-2">
          <Select value={periodFilter} onValueChange={handlePeriodChange}>
            <SelectTrigger className="w-[160px] sm:w-[180px]" data-testid="period-filter">
              <Calendar className="w-4 h-4 mr-2 text-muted-foreground" />
              <SelectValue placeholder="Pilih Periode" />
            </SelectTrigger>
            <SelectContent>
              {periodOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {periodFilter === 'custom' && (
            <Popover open={showCustomPicker} onOpenChange={setShowCustomPicker}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  {customRange.start && customRange.end 
                    ? `${customRange.start} - ${customRange.end}`
                    : 'Pilih Tanggal'
                  }
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 max-w-[calc(100vw-24px)] rounded-2xl" align="end">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Tanggal Mulai</Label>
                    <DatePicker
                      aria-label="Tanggal Mulai" value={customRange.start}
                      onChange={(e) => setCustomRange(prev => ({ ...prev, start: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tanggal Akhir</Label>
                    <DatePicker
                      aria-label="Tanggal Akhir" value={customRange.end}
                      onChange={(e) => setCustomRange(prev => ({ ...prev, end: e.target.value }))}
                    />
                  </div>
                  <Button onClick={handleCustomRangeApply} className="w-full">
                    Terapkan
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard
          icon={DoorOpen}
          value={loading ? '-' : data?.total_rooms || 0}
          label="Total Kamar"
          color="primary"
          loading={loading}
        />
        <StatCard
          icon={Users}
          value={loading ? '-' : data?.total_tenants || 0}
          label="Penghuni Aktif"
          color="accent"
          loading={loading}
        />
        <StatCard
          icon={TrendingUp}
          value={loading ? '-' : formatCurrency(data?.income_this_month || 0)}
          label={periodFilter === 'this_month' ? 'Pendapatan Bulan Ini' : 'Total Pendapatan'}
          color="primary"
          loading={loading}
        />
        <StatCard
          icon={CreditCard}
          value={loading ? '-' : data?.pending_payments || 0}
          label="Belum Bayar"
          color="accent"
          loading={loading}
        />
      </div>

      {/* Prediction & Comparison Cards */}
      {!loading && analytics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Income Prediction */}
          <Card className="p-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-200">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-medium text-purple-600">Prediksi Pendapatan</span>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(analytics.prediction?.predicted_income || 0)}</p>
                <p className="text-sm text-muted-foreground mt-1">{analytics.prediction?.next_month}</p>
              </div>
              <Badge className="bg-purple-100 text-purple-700">
                {analytics.prediction?.confidence === 'medium' ? 'Akurasi Sedang' : 'Estimasi'}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              *Berdasarkan rata-rata pendapatan 6 bulan terakhir dan tren
            </p>
          </Card>

          {/* Month Comparison */}
          <Card className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">vs Bulan Lalu</p>
                <div className="flex items-center gap-2">
                  {analytics.comparison?.change >= 0 ? (
                    <ArrowUpRight className="w-5 h-5 text-green-600" />
                  ) : (
                    <ArrowDownRight className="w-5 h-5 text-red-600" />
                  )}
                  <span className={`text-2xl font-bold ${
                    analytics.comparison?.change >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {analytics.comparison?.change >= 0 ? '+' : ''}{analytics.comparison?.change_percent}%
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {formatCurrency(Math.abs(analytics.comparison?.change || 0))}
                </p>
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                analytics.comparison?.change >= 0 ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {analytics.comparison?.change >= 0 ? (
                  <TrendingUp className="w-6 h-6 text-green-600" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-red-600" />
                )}
              </div>
            </div>
          </Card>

          {/* Payment Stats */}
          <Card className="p-6">
            <div className="flex items-start justify-between mb-4">
              <p className="text-sm font-medium">Status Pembayaran</p>
              <PiggyBank className="w-5 h-5 text-muted-foreground" />
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-sm">Lunas</span>
                </div>
                <span className="font-medium">{analytics.payment_stats?.lunas || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-500" />
                  <span className="text-sm">Menunggu Verifikasi</span>
                </div>
                <span className="font-medium">{analytics.payment_stats?.menunggu_verifikasi || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <span className="text-sm">Belum Bayar</span>
                </div>
                <span className="font-medium">{analytics.payment_stats?.belum_bayar || 0}</span>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Main Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 12 Month Trend Chart */}
        <Card className="lg:col-span-2 p-6">
          <CardHeader className="p-0 pb-6">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg">Tren Pendapatan 12 Bulan</CardTitle>
                <CardDescription>Grafik pendapatan tahunan</CardDescription>
              </div>
              <BarChart3 className="w-5 h-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent className="p-0 h-[300px]">
            {loading ? (
              <Skeleton className="w-full h-full" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={incomeTrendData}>
                  <defs>
                    <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))" 
                    fontSize={11}
                    tickFormatter={(value) => `${(value / 1000000).toFixed(0)}jt`}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Pendapatan']}
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '0.75rem'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="income" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    fill="url(#incomeGradient)" 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="income" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={{ fill: 'hsl(var(--primary))', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Occupancy Rate */}
        <Card className="p-6">
          <CardHeader className="p-0 pb-6">
            <CardTitle className="font-heading text-lg">Tingkat Hunian</CardTitle>
            <CardDescription>Persentase kamar terisi</CardDescription>
          </CardHeader>
          <CardContent className="p-0 space-y-6">
            {loading ? (
              <Skeleton className="w-full h-40" />
            ) : (
              <>
                <div className="flex items-center justify-center">
                  <div className="relative w-32 h-32">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        stroke="hsl(var(--muted))"
                        strokeWidth="12"
                        fill="none"
                      />
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        stroke="hsl(var(--primary))"
                        strokeWidth="12"
                        fill="none"
                        strokeLinecap="round"
                        strokeDasharray={`${(analytics?.room_occupancy?.rate || 0) * 3.52} 352`}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="font-heading text-2xl font-bold">{analytics?.room_occupancy?.rate || 0}%</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-primary" />
                      <span className="text-sm">Terisi</span>
                    </div>
                    <span className="font-medium">{analytics?.room_occupancy?.occupied || 0} kamar</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-muted" />
                      <span className="text-sm">Kosong</span>
                    </div>
                    <span className="font-medium">{analytics?.room_occupancy?.vacant || 0} kamar</span>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Room Occupancy Detail */}
      {!loading && analytics?.room_occupancy?.rooms?.length > 0 && (
        <Card className="p-6">
          <CardHeader className="p-0 pb-6">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg">Status Kamar</CardTitle>
                <CardDescription>Detail hunian per kamar</CardDescription>
              </div>
              <DoorOpen className="w-5 h-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {analytics.room_occupancy.rooms.map((room) => (
                <div 
                  key={room.id}
                  className={`p-4 rounded-xl border-2 ${
                    room.is_occupied 
                      ? 'border-primary/20 bg-primary/5' 
                      : 'border-emerald-200 bg-emerald-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-medium">{room.name}</h4>
                      <p className="text-sm text-muted-foreground">Lantai {room.floor}</p>
                    </div>
                    <Badge className={room.is_occupied ? 'bg-primary/10 text-primary' : 'bg-emerald-100 text-emerald-700'}>
                      {room.is_occupied ? 'Terisi' : 'Kosong'}
                    </Badge>
                  </div>
                  <p className="font-medium text-primary">{formatCurrency(room.price)}/bulan</p>
                  {room.tenant_name && (
                    <p className="text-sm text-muted-foreground mt-1">
                      <Users className="w-3 h-3 inline mr-1" />
                      {room.tenant_name}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expense by Category */}
      {!loading && analytics?.expense_by_category?.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-6">
            <CardHeader className="p-0 pb-6">
              <CardTitle className="font-heading text-lg">Pengeluaran per Kategori</CardTitle>
              <CardDescription>Distribusi biaya operasional tahun ini</CardDescription>
            </CardHeader>
            <CardContent className="p-0 h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analytics.expense_by_category}
                    dataKey="amount"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ label, percent }) => `${label} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {analytics.expense_by_category.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Recent Payments */}
          <Card className="p-6">
            <CardHeader className="p-0 pb-6">
              <CardTitle className="font-heading text-lg">Pembayaran Terakhir</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {data?.recent_payments?.length > 0 ? (
                <div className="space-y-3">
                  {data.recent_payments.slice(0, 5).map((payment) => (
                    <div
                      key={payment.id}
                      className="flex items-center justify-between p-3 rounded-xl bg-muted/50"
                      data-testid={`payment-item-${payment.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          payment.status === 'lunas' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'
                        }`}>
                          {payment.status === 'lunas' ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <AlertCircle className="w-4 h-4" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{payment.tenant_name}</p>
                          <p className="text-xs text-muted-foreground">{payment.room_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-sm">{formatCurrency(payment.amount)}</p>
                        <Badge variant="outline" className="text-xs">
                          {getStatusLabel(payment.status)}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state py-8">
                  <CreditCard className="empty-state-icon w-8 h-8" />
                  <p className="empty-state-title text-sm">Belum ada pembayaran</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

const TenantDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await axios.get(`${API_URL}/api/reports/dashboard`);
      setData(response.data);
    } catch (error) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="tenant-dashboard">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Halo, {user?.name}!</h1>
        <p className="text-muted-foreground mt-1">Selamat datang kembali</p>
      </div>

      <div className="flex flex-wrap gap-3" aria-label="Akses cepat">
        <Button asChild><Link to="/pembayaran">Tagihan & pembayaran</Link></Button>
        <Button asChild variant="outline"><Link to="/kamar-saya">Kamar saya</Link></Button>
        <Button asChild variant="outline"><Link to="/pengaduan">Pengaduan</Link></Button>
      </div>
      {loadError ? <Card className="p-6" role="alert"><p className="font-semibold">Dashboard belum berhasil dimuat</p><p className="text-muted-foreground mt-2">Periksa koneksi Anda lalu coba lagi.</p><Button className="mt-4" onClick={fetchDashboard}>Coba lagi</Button></Card> : loading ? (
        <div className="space-y-6">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : !data?.tenant ? (
        <Card className="p-8 text-center">
          <div className="empty-state">
            <Home className="empty-state-icon" />
            <p className="empty-state-title">Akun Anda belum terhubung dengan kamar</p>
            <p className="empty-state-description">Pilih kamar melalui menu Kamar Saya atau hubungi Tim Kos Cemara.</p>
          </div>
        </Card>
      ) : (
        <>
          {/* Room Info */}
          <Card className="overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="aspect-[4/3] md:aspect-auto">
                <RoomGallery
                  room={data.room}
                  alt={data.room?.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 flex flex-col justify-center">
                <Badge className="w-fit mb-4 status-terisi">{data.room?.status === 'terisi' ? 'Kamar Anda' : ''}</Badge>
                <h2 className="font-heading text-2xl font-bold">{data.room?.name || 'Informasi kamar belum tersedia'}</h2>
                <p className="text-muted-foreground mt-1">{data.room?.floor != null ? `Lantai ${data.room.floor}` : 'Hubungi Tim Kos Cemara untuk informasi kamar'}</p>
                <p className="font-heading text-xl font-bold text-primary mt-4">
                  {formatCurrency(data.room?.price)}<span className="text-sm font-normal text-muted-foreground">/bulan</span>
                </p>
                {data.room?.facilities?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {data.room.facilities.map((f, i) => (
                      <Badge key={i} variant="secondary">{f}</Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <StatCard
              icon={Calendar}
              value={data.tenant?.start_date ? new Date(data.tenant.start_date).toLocaleDateString('id-ID', { month: 'short', year: 'numeric' }) : '-'}
              label="Mulai Sewa"
              color="primary"
            />
            <StatCard
              icon={CreditCard}
              value={data.pending_count || 0}
              label="Tagihan Belum Bayar"
              color="accent"
            />
            <StatCard
              icon={TrendingUp}
              value={formatCurrency(data.total_pending || 0)}
              label="Total Tagihan"
              color="accent"
            />
          </div>

          {data.verification_count > 0 && <Card className="p-4 border-amber-200"><p>{data.verification_count} pembayaran sedang menunggu verifikasi Tim Kos Cemara.</p></Card>}
          {/* Recent Payments */}
          <Card className="p-6">
            <CardHeader className="p-0 pb-6">
              <CardTitle className="font-heading text-lg">Riwayat Pembayaran Terbaru</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {data.payments?.length > 0 ? (
                <div className="space-y-3">
                  {data.payments.map((payment) => (
                    <div
                      key={payment.id}
                      className="flex flex-wrap gap-3 items-center justify-between p-4 rounded-xl bg-muted/50"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          payment.status === 'lunas' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'
                        }`}>
                          {payment.status === 'lunas' ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            <AlertCircle className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{formatMonth(payment.month)}</p>
                          <p className="text-sm text-muted-foreground">{getPaymentTypeLabel(payment.payment_type)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatCurrency(payment.amount)}</p>
                        <Badge variant={payment.status === 'lunas' ? 'default' : 'secondary'} className={`status-${payment.status}`}>
                          {getStatusLabel(payment.status)}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <CreditCard className="empty-state-icon" />
                  <p className="empty-state-title">Belum ada pembayaran</p>
                  <p className="empty-state-description">Riwayat pembayaran akan muncul di sini</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

const Dashboard = () => {
  const { canManageKos } = useAuth();
  return canManageKos() ? <OwnerDashboard /> : <TenantDashboard />;
};

export default Dashboard;
